import React, { ReactElement } from 'react'
import styles from '../Filters.module.scss'
import { FilterChip } from 'components/SA/Filters'
import { getPriceLabel, getPriceWithCurrencySymbol } from '../helpers'
import { SelectedFacetConnector, SelectedFacetConnectorProps } from 'connectors/Facets'
import { LocaleCurrency } from 'types/Shared/Locale'
import { SelectedFacets } from 'types/SearchProvider'
import {
  OVERRIDE_STATUS_MAPPING_RULES,
  OVERRIDE_SORT_MAPPING_RULES,
  OVERRIDE_MATERIALS_MAPPING_RULES,
  OVERRIDE_ORIENTATION_MAPPING_RULES,
  OVERRIDE_IS_OUTDOOR_MAPPING_RULES,
  OVERRIDE_CURATED_STATUS_MAPPING_RULES,
  OVERRIDE_READY_TO_HANG_MAPPING_RULES,
  OVERRIDE_FRAMED_MAPPING_RULES,
  OVERRIDE_OUTDOOR_SAFE_MAPPING_RULES,
  OVERRIDE_PACKAGING_MAPPING_RULES,
  OVERRIDE_ORIGINAL_AVAILABILITY_STATUS_MAPPING_RULES,
  OVERRIDE_CATEGORY_MAPPING_RULES,
  OVERRIDE_INSTALLATION_TYPE_MAPPING_RULES
} from 'components/SA/Facets/helpers/constants'
import { capitalizeString } from 'lib/helpers'
import { convertSizeUnit } from 'components/SA/Facets/helpers/facets'
import { MeasurementUnit } from 'types/User/Session'
import { usStateCodes } from 'lib/countryStates'
import classNames from 'classnames'

type FacetTagParams = SelectedFacets & {
  selectedCurrency: LocaleCurrency
  measurementUnit: MeasurementUnit
}

const getFacetTag = ({
  facetName,
  title,
  attributeName,
  selectedCurrency,
  measurementUnit,
}: FacetTagParams) => {
  switch (facetName) {
    case 'color':
      return <div className={styles.colorFacet} style={{ backgroundColor: attributeName }} />

    case 'price':
    case 'prints_price_ranges':
    case 'geo_prices':
    case 'print_all_prices':
    case 'print_all_prices_values':
      return getPriceLabel(selectedCurrency, title)

    case 'custom__geo_prices':
    case 'custom__print_all_prices':
      return getPriceWithCurrencySymbol(selectedCurrency, title)

    case 'original_status':
      return OVERRIDE_STATUS_MAPPING_RULES[title]

    case 'size':
    case 'size_bin':
    case 'print_size':
    case 'print_size_bins':
      const OVERRIDE_SIZE_MAPPING_RULES = convertSizeUnit(measurementUnit)
      return OVERRIDE_SIZE_MAPPING_RULES[title]

    case 'sort':
      return OVERRIDE_SORT_MAPPING_RULES[title]

    case 'materials':
    case 'prints_materials':
    case 'print_materials':
      return OVERRIDE_MATERIALS_MAPPING_RULES[title] || capitalizeString(title)

    case 'orientation':
    case 'print_orientations':
      return OVERRIDE_ORIENTATION_MAPPING_RULES[title] || capitalizeString(title)

    case 'is_outdoor':
    case 'is_outdoor_safe':
      return (
        capitalizeString(OVERRIDE_IS_OUTDOOR_MAPPING_RULES[facetName]) ||
        OVERRIDE_OUTDOOR_SAFE_MAPPING_RULES[facetName] ||
        ''
      )

    case 'is_framed':
      return OVERRIDE_FRAMED_MAPPING_RULES[attributeName] || ''

    case 'is_ready_to_hang':
      return OVERRIDE_READY_TO_HANG_MAPPING_RULES[attributeName] || ''

    case 'width':
      return `W ${title}`

    case 'height':
      return `H ${title}`

    case 'curated_status_value':
    case 'curated_status':
      return OVERRIDE_CURATED_STATUS_MAPPING_RULES[`${title}`]

    case 'us_state':
      return (
        usStateCodes.find(({ abbreviation }) => abbreviation === title?.toUpperCase())?.name || ''
      )

    case 'packaging':
      return OVERRIDE_PACKAGING_MAPPING_RULES[`${title}`]

    case 'panels':
      return `Panels: ${title}`

    case 'original_availability_status':
    case 'print_availability_status':
      return OVERRIDE_ORIGINAL_AVAILABILITY_STATUS_MAPPING_RULES[title] || capitalizeString(title)

    case 'category':
    case 'group_id':
      return OVERRIDE_CATEGORY_MAPPING_RULES[title] || capitalizeString(title)

    case 'installation_type':
      return OVERRIDE_INSTALLATION_TYPE_MAPPING_RULES[title] || capitalizeString(title)

    default:
      return capitalizeString(title)
  }
}

type Chip = SelectedFacets & {
  [key: string]: any
}

type ChipListFilterProps<T> = SelectedFacetConnectorProps & {
  facetsList: ReadonlyArray<T>
  onClose: (facet: T) => void
  onClearFilter: () => void
  isClearFilterVisible?: boolean
  customChipListFilterStyles?: string
}

function ChipListFilter<T extends Chip>({
  facetsList,
  onClose,
  onClearFilter,
  isClearFilterVisible,
  selectedCurrency,
  measurementUnit,
  customChipListFilterStyles = '',
}: ChipListFilterProps<T>): ReactElement<ChipListFilterProps<T>> {
  return (
    <div
      className={classNames(styles.chipListFilterContainer, {
        [customChipListFilterStyles]: customChipListFilterStyles !== '',
      })}
      key={'chip-list-filter'}
    >
      {facetsList?.length > 0 && isClearFilterVisible && (
        <button onClick={onClearFilter}>Clear All</button>
      )}
      {facetsList.map((facet, index) => (
        <FilterChip
          key={`${facet.title}-${index}`}
          keyValue={`${facet.title}-${index}`}
          title={getFacetTag({
            facetName: facet.facetName,
            title: facet.title,
            attributeName: facet.attributeName,
            selectedCurrency,
            measurementUnit,
          })}
          onClose={() => onClose(facet)}
        />
      ))}
    </div>
  )
}

export default SelectedFacetConnector(ChipListFilter)
